import { cn } from "@gymflow/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useClubSettings } from "../../../../providers";
import { Button, CheckInIcon } from "../../../atoms";
import { AppPreviewAppointmentsIcon } from "../../../atoms/icons/AppPreviewAppointmentsIcon";
import { AppPreviewClassesIcon } from "../../../atoms/icons/AppPreviewClassesIcon";
import { AppPreviewHomeIcon } from "../../../atoms/icons/AppPreviewHomeIcon";
import { AppPreviewProfileIcon } from "../../../atoms/icons/AppPreviewProfileIcon";
import { AppPreviewShopIcon } from "../../../atoms/icons/AppPreviewShopIcon";
import AppointmentPreview from "../AppSettingsHomePage/PreviewWidgets/AppointmentPreview.png";
import { AttendanceWidget } from "../AppSettingsHomePage/PreviewWidgets/AttendanceWidget";
import ClassPreview from "../AppSettingsHomePage/PreviewWidgets/ClassPreview.png";
import { CTACreditPack } from "../AppSettingsHomePage/PreviewWidgets/CTACreditPack";
import { CTAMembership } from "../AppSettingsHomePage/PreviewWidgets/CTAMembership";
import { UpcomingBookings } from "../AppSettingsHomePage/PreviewWidgets/UpcomingClasses";
import { AppSettingsContext } from "../components/AppSettingsContext";

export const AppSettingsNavigationPreview = ({
  className,
}: {
  className?: string;
}) => {
  const { t } = useTranslation();
  const { featureFlags } = useClubSettings();
  const { navigationSettings, homePageSettings } =
    useContext(AppSettingsContext);
  const amountEnabled =
    Object.values(navigationSettings).filter(Boolean).length + 2;

  const ctaComponents = useMemo(() => {
    const components = {
      buyMembership: <CTAMembership key="buyMembership" />,
      buyCreditPack: <CTACreditPack key="buyCreditPack" />,
    };
    return homePageSettings.ctas
      .filter((cta) => cta.enabled)
      .map((cta) => components[cta.id]);
  }, [homePageSettings.ctas]);
  const widgetComponents = useMemo(() => {
    const components = {
      appointmentBooking: (
        <UpcomingBookings
          key="appointmentBooking"
          previewImage={AppointmentPreview}
          title={t(
            "pages.settings.appSettings.homePage.upcomingAppointments.title",
          )}
        />
      ),
      classBooking: (
        <UpcomingBookings
          key="classBooking"
          previewImage={ClassPreview}
          title={t("pages.settings.appSettings.homePage.upcomingClasses.title")}
        />
      ),
      gymAttendance: (
        <AttendanceWidget key="gymAttendance" type="gymAttendance" />
      ),
      classAttendance: (
        <AttendanceWidget key="classAttendance" type="classAttendance" />
      ),
      appointmentAttendance: (
        <AttendanceWidget
          key="appointmentAttendance"
          type="appointmentAttendance"
        />
      ),
    };
    if (!featureFlags.MEMBER_APP_HOME_PAGE_CUSTOMIZATION) {
      const result: ReactNode[] = [];
      if (navigationSettings.appointmentsNavigationEnabled) {
        result.push(components.appointmentBooking);
      }
      if (navigationSettings.classesNavigationEnabled) {
        result.push(components.classBooking);
      }
      return result;
    }
    return homePageSettings.widgets
      .filter((widget) => widget.enabled)
      .map((widget) => {
        const component = components[widget.id as keyof typeof components];
        if (!component) return undefined;
        return component;
      })
      .filter(Boolean);
  }, [
    featureFlags.MEMBER_APP_HOME_PAGE_CUSTOMIZATION,
    homePageSettings.widgets,
    navigationSettings.appointmentsNavigationEnabled,
    navigationSettings.classesNavigationEnabled,
    t,
  ]);
  const isEmpty = widgetComponents.length === 0 && ctaComponents.length === 0;
  if (!navigationSettings) return null;

  return (
    <div
      className={cn(
        "flex size-full items-center justify-center rounded-3xl bg-gray-100 p-6",
        className,
      )}
    >
      <div className="bg-gray-0 relative flex h-fit max-h-[calc(min(45rem,100%))] w-full max-w-80 flex-col overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
        <div className="flex w-full flex-col overflow-y-auto overflow-x-clip">
          <div className="flex flex-col border-b border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-950">
              {t("page.settings.appSettings.preview.title")}
            </div>
            <div className="text-base font-semibold text-gray-500">
              {t("page.settings.appSettings.preview.description")}
            </div>
          </div>
          <div className="flex min-h-[min(34rem,50vh)] flex-col items-center p-4">
            <motion.div
              layout
              className=" flex size-full max-w-full flex-col items-center gap-2"
            >
              {isEmpty && (
                <div className="flex max-w-64 flex-col items-center gap-1.5 pt-24">
                  <div className="font-semibold text-gray-950">
                    {t("page.settings.appSettings.preview.empty")}
                  </div>
                  <div className="text-center text-sm font-medium text-gray-500">
                    {t("page.settings.appSettings.preview.emptyDescription")}
                  </div>
                </div>
              )}
              {featureFlags.MEMBER_APP_HOME_PAGE_CUSTOMIZATION && (
                <>
                  <motion.div
                    layout
                    className="relative flex w-full flex-row gap-2 overflow-x-visible"
                  >
                    <AnimatePresence>{ctaComponents}</AnimatePresence>
                  </motion.div>
                  <AnimatePresence>
                    {ctaComponents.length > 1 && (
                      <motion.div
                        className="flex flex-row gap-1"
                        layout
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <div className="bg-secondary-500 size-2 rounded-full"></div>
                        <div className="size-2 rounded-full bg-gray-200"></div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}

              {widgetComponents}
            </motion.div>
          </div>
        </div>
        <AnimatePresence>
          {featureFlags.MEMBER_APP_HOME_PAGE_CUSTOMIZATION &&
            homePageSettings.checkInWidgetEnabled.enabled && (
              <motion.div
                layout
                initial={{ opacity: 0, y: 8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="absolute bottom-16 right-4"
              >
                <Button intent="gradient" className="gap-2 px-6">
                  <CheckInIcon className="stroke-white" />
                  {t("page.settings.appSettings.homePage.checkIn")}
                </Button>
              </motion.div>
            )}
        </AnimatePresence>
        <motion.div className="flex w-full min-w-fit flex-row items-center justify-around gap-2 border-t border-gray-200 bg-white p-2">
          <Option className="bg-secondary-50 rounded-lg">
            <AppPreviewHomeIcon className="stroke-secondary-600 size-4" />
            {amountEnabled <= 3 && (
              <OptionLabel className="text-secondary-600">
                {t("page.settings.appSettings.navigation.homeIcon")}
              </OptionLabel>
            )}
          </Option>
          {navigationSettings.classesNavigationEnabled && (
            <Option>
              <AppPreviewClassesIcon className="size-4 fill-gray-500" />
              {amountEnabled <= 3 && (
                <OptionLabel>
                  {t("page.settings.appSettings.navigation.classesIcon")}
                </OptionLabel>
              )}
            </Option>
          )}
          {navigationSettings.appointmentsNavigationEnabled && (
            <Option>
              <AppPreviewAppointmentsIcon className="size-4 stroke-gray-500" />
              {amountEnabled <= 3 && (
                <OptionLabel>
                  {t("page.settings.appSettings.navigation.appointmentsIcon")}
                </OptionLabel>
              )}
            </Option>
          )}
          {navigationSettings.shopNavigationEnabled && (
            <Option>
              <AppPreviewShopIcon className="size-4 stroke-gray-500" />
              {amountEnabled <= 3 && (
                <OptionLabel>
                  {t("page.settings.appSettings.navigation.shopIcon")}
                </OptionLabel>
              )}
            </Option>
          )}
          <Option>
            <AppPreviewProfileIcon className="size-4 stroke-gray-500" />
            {amountEnabled <= 3 && (
              <OptionLabel>
                {t("page.settings.appSettings.navigation.profileIcon")}
              </OptionLabel>
            )}
          </Option>
        </motion.div>
      </div>
    </div>
  );
};

const Option = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.div
      layout
      className={cn(
        "flex flex-row items-center gap-1 p-1 py-2 flex-1 justify-center",
        className,
      )}
    >
      {children}
    </motion.div>
  );
};
const OptionLabel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.div
      className={cn("text-xs font-medium text-gray-500", className)}
      layout
    >
      {children}
    </motion.div>
  );
};
